import {useDebounce} from '@github-ui/use-debounce'
import {SearchIcon, XCircleFillIcon} from '@primer/octicons-react'
import {FormControl, TextInput} from '@primer/react'
import {useEffect, useState} from 'react'

import styles from './RepositoryProjects.module.css'

interface ProjectSearchInputProps {
  /** Current query value from the URL (`?q=`). */
  query: string
  /** Called with the new query, debounced while the user types. */
  onQueryChange: (query: string) => void
}

const LABEL = 'Search all projects'
const DEBOUNCE_MS = 500

export function ProjectSearchInput({query, onQueryChange}: ProjectSearchInputProps) {
  const [value, setValue] = useState(query)

  // Sync local value when the URL changes externally (e.g. back/forward navigation).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setValue(query)
  }, [query])

  const debouncedOnQueryChange = useDebounce(onQueryChange, DEBOUNCE_MS)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value
    setValue(next)
    debouncedOnQueryChange(next)
  }

  const handleClear = () => {
    setValue('')
    // Clear immediately, no debounce.
    debouncedOnQueryChange.cancel()
    onQueryChange('')
  }

  return (
    <FormControl className={styles.searchInputWrapper}>
      <FormControl.Label visuallyHidden>{LABEL}</FormControl.Label>
      <TextInput
        block
        type="search"
        value={value}
        onChange={handleChange}
        placeholder={LABEL}
        leadingVisual={SearchIcon}
        trailingAction={
          value ? <TextInput.Action onClick={handleClear} icon={XCircleFillIcon} aria-label="Clear" /> : undefined
        }
      />
    </FormControl>
  )
}
